<template>
  <header class="top-bar" :data-scrolled="isScrolled">
    <div class="top-bar__inner">
      <div class="top-bar__left">
        <router-link to="/" class="top-bar__brand" :aria-label="brandName">
          <img :src="iconUrl" :alt="brandName + ' logo'" class="top-bar__logo" width="44" height="44" />
          <span class="top-bar__brand-name">{{ brandName }}</span>
        </router-link>
      </div>
      <nav class="top-bar__nav" :aria-label="brandName">
        <router-link to="/" class="top-bar__nav-link">
          {{ $t("navigation.home") }}
        </router-link>
        <router-link
          v-if="!authState.isAuthenticated"
          to="/signin"
          class="top-bar__nav-link"
        >
          {{ $t("topBar.signIn") }}
        </router-link>
        <router-link v-else to="/" class="top-bar__nav-link">
          {{ $t("topBar.openApp") }}
        </router-link>
        <div class="top-bar__lang" @click.stop>
          <button
            type="button"
            class="top-bar__lang-button"
            :aria-label="currentLang.name"
            aria-haspopup="listbox"
            :aria-expanded="langOpen"
            @click="langOpen = !langOpen"
          >
            <span class="top-bar__lang-flag" aria-hidden="true">{{ currentLang.flag }}</span>
            <span class="top-bar__lang-caret" aria-hidden="true">▾</span>
          </button>
          <ul v-if="langOpen" class="top-bar__lang-menu" role="listbox">
            <li
              v-for="lang in LANGUAGES"
              :key="lang.code"
              role="option"
              :aria-selected="lang.code === $i18n.locale"
              class="top-bar__lang-item"
              :class="{ 'top-bar__lang-item--active': lang.code === $i18n.locale }"
              @click="selectLang(lang.code)"
            >
              <span class="top-bar__lang-flag" aria-hidden="true">{{ lang.flag }}</span>
              <span class="top-bar__lang-name">{{ lang.name }}</span>
            </li>
          </ul>
        </div>
      </nav>
      <div class="top-bar__right">
        <a :href="appStoreUrl" class="top-bar__store" aria-label="App Store">
          <img :src="appStoreBadge" alt="Download on the App Store" class="top-bar__store-img top-bar__store-img--ios" />
        </a>
        <a :href="playStoreUrl" class="top-bar__store" aria-label="Google Play">
          <img :src="playStoreBadge" alt="Get it on Google Play" class="top-bar__store-img top-bar__store-img--android" />
        </a>
      </div>
    </div>
  </header>
</template>

<script>
import { authState } from "../stores/authStore";
import { setLanguage } from "../i18n";
import iconUrl from "../assets/icon.png";
import appStoreBadge from "../assets/ios.svg";
import playStoreBadge from "../assets/android.png";

const LANGUAGES = [
  { code: "en", flag: "🇬🇧", name: "English" },
  { code: "fr", flag: "🇫🇷", name: "Français" },
  { code: "de", flag: "🇩🇪", name: "Deutsch" },
  { code: "es", flag: "🇪🇸", name: "Español" },
  { code: "it", flag: "🇮🇹", name: "Italiano" },
  { code: "pt", flag: "🇵🇹", name: "Português" },
  { code: "nl", flag: "🇳🇱", name: "Nederlands" },
  { code: "sv", flag: "🇸🇪", name: "Svenska" },
  { code: "pl", flag: "🇵🇱", name: "Polski" },
  { code: "cs", flag: "🇨🇿", name: "Čeština" },
  { code: "ro", flag: "🇷🇴", name: "Română" },
  { code: "el", flag: "🇬🇷", name: "Ελληνικά" },
  { code: "tr", flag: "🇹🇷", name: "Türkçe" },
  { code: "uk", flag: "🇺🇦", name: "Українська" },
  { code: "ru", flag: "🇷🇺", name: "Русский" },
  { code: "ar", flag: "🇸🇦", name: "العربية" },
  { code: "hi", flag: "🇮🇳", name: "हिन्दी" },
  { code: "ja", flag: "🇯🇵", name: "日本語" },
];

export default {
  name: "AppTopBar",
  data() {
    return {
      authState,
      isScrolled: false,
      iconUrl,
      appStoreBadge,
      playStoreBadge,
      LANGUAGES,
      langOpen: false,
    };
  },
  computed: {
    brandName() {
      const rawLocale = this.$i18n?.locale;
      const locale = (typeof rawLocale === "string" ? rawLocale : rawLocale?.value) || "en";
      return locale.toLowerCase().startsWith("fr") ? "Wellnotes" : "Wellnotes";
    },
    appStoreUrl() {
      return this.$t("home.appStoreUrl");
    },
    playStoreUrl() {
      return "https://play.google.com/store/apps/details?id=com.antoine.cogito";
    },
    currentLang() {
      const code = this.$i18n?.locale || "en";
      return LANGUAGES.find((l) => l.code === code) || LANGUAGES[0];
    },
  },
  mounted() {
    if (typeof window !== "undefined") {
      window.addEventListener("scroll", this.handleScroll, { passive: true });
      window.addEventListener("click", this.closeLangMenu);
      this.handleScroll();
    }
  },
  beforeUnmount() {
    if (typeof window !== "undefined") {
      window.removeEventListener("scroll", this.handleScroll);
      window.removeEventListener("click", this.closeLangMenu);
    }
  },
  methods: {
    handleScroll() {
      this.isScrolled = window.scrollY > 8;
    },
    selectLang(code) {
      setLanguage(code);
      this.langOpen = false;
    },
    closeLangMenu() {
      this.langOpen = false;
    },
  },
};
</script>

<style scoped>
.top-bar {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  display: flex;
  justify-content: center;
  background: color-mix(in srgb, var(--app-bg) 55%, transparent);
  backdrop-filter: saturate(180%) blur(18px);
  -webkit-backdrop-filter: saturate(180%) blur(18px);
  border-bottom: 1px solid transparent;
  transition: background-color 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease;
}

.top-bar[data-scrolled="true"] {
  background: color-mix(in srgb, var(--app-bg) 78%, transparent);
  border-bottom-color: var(--app-border);
  box-shadow: 0 1px 0 rgba(0, 0, 0, 0.04);
}

.top-bar__inner {
  width: 100%;
  max-width: 1180px;
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  align-items: center;
  padding: 10px 22px;
  gap: 16px;
}

.top-bar__left {
  display: flex;
  align-items: center;
  gap: 10px;
  justify-self: start;
}

.top-bar__nav {
  justify-self: center;
}

.top-bar__right {
  justify-self: end;
}

.top-bar__brand {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  text-decoration: none;
  color: var(--app-heading);
}

.top-bar__logo {
  width: 44px;
  height: 44px;
  display: block;
}

.top-bar__brand-name {
  font-size: 20px;
  font-weight: 700;
  letter-spacing: -0.01em;
  color: var(--app-heading);
}

.top-bar__nav {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.top-bar__nav-link {
  display: inline-flex;
  align-items: center;
  height: 36px;
  padding: 0 12px;
  border-radius: 999px;
  color: var(--app-text);
  font-size: 15px;
  font-weight: 600;
  text-decoration: none;
  transition: background-color 0.18s ease, color 0.18s ease;
}

.top-bar__nav-link:hover {
  background: var(--app-surface-strong);
  color: var(--app-heading);
}

.top-bar__lang {
  position: relative;
  display: inline-flex;
  align-items: center;
}

.top-bar__lang-button {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  height: 36px;
  padding: 0 10px;
  border: none;
  border-radius: 999px;
  background: transparent;
  color: var(--app-text);
  font: inherit;
  cursor: pointer;
  transition: background-color 0.18s ease;
}

.top-bar__lang-button:hover {
  background: var(--app-surface-strong);
}

.top-bar__lang-flag {
  font-size: 18px;
  line-height: 1;
}

.top-bar__lang-caret {
  font-size: 11px;
  opacity: 0.6;
}

.top-bar__lang-menu {
  position: absolute;
  top: calc(100% + 6px);
  right: 0;
  min-width: 180px;
  max-height: 60vh;
  overflow-y: auto;
  margin: 0;
  padding: 6px;
  list-style: none;
  background: var(--app-surface);
  border: 1px solid var(--app-border);
  border-radius: 12px;
  box-shadow: var(--app-shadow);
  z-index: 1100;
}

.top-bar__lang-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 10px;
  border-radius: 8px;
  font-size: 14px;
  color: var(--app-text);
  cursor: pointer;
  transition: background-color 0.15s ease;
}

.top-bar__lang-item:hover {
  background: var(--app-surface-strong);
}

.top-bar__lang-item--active {
  background: var(--app-surface-strong);
  font-weight: 600;
}

.top-bar__lang-name {
  white-space: nowrap;
}

.top-bar__right {
  display: flex;
  align-items: center;
  gap: 8px;
}

.top-bar__store {
  display: inline-flex;
  align-items: center;
  text-decoration: none;
}

.top-bar__store-img {
  display: block;
}

.top-bar__store-img--ios {
  height: 38px;
  width: auto;
}

.top-bar__store-img--android {
  height: 56px;
  width: auto;
  margin: -9px -6px;
}

@media (max-width: 860px) {
  .top-bar__brand-name {
    display: none;
  }
  .top-bar__store-img--ios {
    height: 32px;
  }
  .top-bar__store-img--android {
    height: 48px;
  }
}

@media (max-width: 600px) {
  .top-bar__nav {
    gap: 2px;
  }
  .top-bar__nav-link {
    padding: 0 8px;
    font-size: 14px;
  }
  .top-bar__right {
    display: none;
  }
}

@media (max-width: 480px) {
  .top-bar__inner {
    padding: 8px 14px;
  }
  .top-bar__logo {
    width: 36px;
    height: 36px;
  }
}
</style>
